import { getIn } from 'formik';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { Box } from '@hivebrite/design-system/Box';
import { CardWithTitle } from '@hivebrite/design-system/Card';
import { FakeLink } from '@hivebrite/design-system/Link';
import { Section } from '@hivebrite/design-system/Section';

import { type GuestApi } from 'hb-react/shared/apps/Events/contexts/GuestsContext';
import {
  GUEST_OPTIONS_CURRENT_USER_VALUE,
  GUEST_OPTIONS_OTHER_VALUE,
} from 'hb-react/shared/apps/Events/hooks';
import { type TicketItem } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { StandardFields } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/components/Form/components/StandardFields';
import { TicketAddons } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/components/Form/components/TicketAddons';
import { useAttendeeSync } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/hooks/useAttendeeSync';
import { useForm } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/hooks/useForm';
import { type Attendee } from 'hb-react/shared/apps/Events/types';
import {
  Checkbox,
  CustomAttribute,
  Email,
  Radio,
  Text,
} from 'hb-react/shared/components/formik/components/inputs';
import { ReactSelectUsers } from 'hb-react/shared/components/ReactSelect';
import { type User } from 'hb-react/types';

import GuestSelect from '../GuestSelect';

const TicketHolder = ({
  index,
  onChange,
}: {
  index: number | string;
  onChange: (type: string) => void;
}) => {
  const id = useId();

  return (
    <Radio
      id={id}
      inputs={[
        {
          value: 'current_user',
          label: s__('AddAttendee|Myself'),
        },
        {
          value: 'guest',
          label: s__('Events|External Guest'),
        },
        {
          value: 'internal_user',
          label: s__('Events|Internal Community Member'),
        },
      ]}
      name={`attendees[${index}][ticket_holder]`}
      validate={{
        required: true,
      }}
      type="input"
      direction="vertical"
      onChange={onChange}
      mb={0}
    />
  );
};

export const InternalUserRegistration = ({
  currentUser,
  attendee,
  ticket,
  index,
  guestsOptions,
  deleteGuest,
  updateGuest,
  createGuest,
}: {
  attendee: Attendee;
  createGuest: GuestApi['create'];
  currentUser: User;
  deleteGuest: GuestApi['delete'];
  guestsOptions: any;
  index: number;
  ticket: TicketItem;
  updateGuest: GuestApi['update'];
}) => {
  const { event } = useBooking();

  const { syncAttendeeField } = useAttendeeSync();

  const [expanded, setExpanded] = useState(false);

  const { setFieldValue, values } = useForm();

  const attendees = values?.attendees;

  const id = useId();
  const guestsOptionsWithoutCurrentUser = useMemo(
    () =>
      guestsOptions.filter(
        ({ value }) => value !== GUEST_OPTIONS_CURRENT_USER_VALUE,
      ),
    [guestsOptions],
  );

  const ticketType = attendee.ticket_holder;

  const { customizable_attributes: customizableAttributes } = ticket;
  const standardFields = event?.standard_fields;

  useEffect(() => {
    if (attendee?.guest_id === null && expanded) {
      setExpanded(false);
    } else if (attendee?.guest_id && !expanded) {
      setExpanded(true);
    }
  }, [attendee]);

  const formFields = useMemo(
    () => [
      'metadata.event.photo_url',
      'id',
      'email',
      'firstname',
      'lastname',
      'ticket_holder',
      'user_id',
      'guest_id',
      ...[
        ...Object.values(standardFields || {}),
        ...customizableAttributes,
      ].map(({ name }) => name),
    ],
    [standardFields, customizableAttributes],
  );

  const setFieldValueForUser = useCallback(
    (user, i = index) => {
      formFields.forEach((field) => {
        setFieldValue(`attendees.${i}.${field}`, getIn(user, field, ''));
      });
    },
    [index, formFields, setFieldValue],
  );

  const clearValue = useCallback(
    (i = index, resetGuest = false) => {
      formFields.forEach(
        (field) =>
          field !== 'ticket_holder' &&
          setFieldValue(`attendees[${i}][${field}]`, '', false),
      );

      if (resetGuest) {
        setFieldValue(`attendees[${i}][guest_id]`, GUEST_OPTIONS_OTHER_VALUE);
      }
    },
    [formFields, index, setFieldValue],
  );

  const onGuestChange = useCallback(
    ({ uuid, ...value }) => {
      if (value?.value === GUEST_OPTIONS_OTHER_VALUE) {
        return setFieldValueForUser({
          guest_id: GUEST_OPTIONS_OTHER_VALUE,
          ticket_holder: 'guest',
        });
      }
      setFieldValueForUser({ ...value, ticket_holder: 'guest' });
    },
    [setFieldValueForUser],
  );

  const onUpdate = (payload) => {
    if (
      attendee.guest_id === GUEST_OPTIONS_CURRENT_USER_VALUE ||
      attendee.ticket_holder === 'internal_user'
    ) {
      return;
    }

    if (attendee.guest_id && attendee.guest_id !== GUEST_OPTIONS_OTHER_VALUE) {
      return updateGuest({
        guest: {
          ...attendee,
          ...payload,
        },
      });
    }

    const guestId = uuid();

    setFieldValue(`attendees[${index}][guest_id]`, guestId);

    return createGuest({
      guest: { ...attendee, ...payload, guest_id: guestId },
    });
  };

  const onDelete = useCallback(
    (guest) => {
      attendees
        .map((attendee, i) => (attendee.guest_id === guest.guest_id ? i : ''))
        .filter(String)
        .forEach((ticketIndex) => clearValue(Number(ticketIndex), true));

      deleteGuest({ guest });
    },
    [attendees, clearValue],
  );

  const onPrefill = useCallback(() => {
    attendees.forEach((_, i) => {
      if (i === index) {
        return;
      }

      setFieldValueForUser(attendee, i);
    });
  }, [attendees, index, setFieldValueForUser, attendee]);

  const onTicketHolderChange = useCallback(
    (value) => {
      clearValue();

      if (value === 'current_user') {
        setFieldValueForUser({
          ...currentUser,
          ticket_holder: 'current_user',
          user_id: currentUser.id,
          guest_id: GUEST_OPTIONS_CURRENT_USER_VALUE,
        });
      }
    },
    [clearValue, setFieldValueForUser, currentUser],
  );

  const onUserSelect = ({ data: user }) => {
    setFieldValueForUser({
      ...user,
      firstname: user?.full_name ?? user?.firstname,
      ticket_holder: 'internal_user',
      user_id: user.id,
      guest_id: '',
      metadata: {
        event: {
          photo_url: user?.photo_url,
        },
      },
    });
  };

  useEffect(() => {
    if (!attendee.ticket_holder) {
      setFieldValue(
        `attendees[${index}][ticket_holder]`,
        'current_user',
        false,
      );
      onTicketHolderChange('current_user');

      return;
    }

    if (
      guestsOptionsWithoutCurrentUser.length === 1 &&
      attendee.ticket_holder === 'guest'
    ) {
      setFieldValue(`attendees[${index}][guest_id]`, GUEST_OPTIONS_OTHER_VALUE);

      return;
    }

    const isValidGuest =
      attendee.ticket_holder === 'guest' && !!attendee?.guest_id;

    const isValidUser =
      attendee.ticket_holder &&
      ['internal_user', 'current_user'].includes(attendee.ticket_holder) &&
      (attendee?.user_id || attendee?.id);

    setExpanded(Boolean(isValidGuest || isValidUser));
  }, [
    attendee,
    index,
    onTicketHolderChange,
    setFieldValue,
    guestsOptionsWithoutCurrentUser,
  ]);

  return (
    <CardWithTitle
      title={
        <>
          {__('Ticket %{index} • ').i__({ index: `${index + 1}` })}
          {ticket.name}
        </>
      }
      data-testid="event-ticket-guest"
    >
      <Box flexDirection="column">
        <Section containerPaddingX={false}>
          <Box
            gridColumn={{
              _: 'auto',
              m: '1 / 3',
            }}
            flexDirection="column"
          >
            <TicketHolder
              index={index}
              onChange={(type) => {
                onTicketHolderChange(type);
              }}
            />
            {ticketType === 'guest' && (
              <GuestSelect
                name={`attendees[${index}][guest_id]`}
                undeletable={[__('Add an external guest')]}
                guestsOptions={guestsOptionsWithoutCurrentUser}
                onChange={onGuestChange}
                onDelete={onDelete}
                wrapperProps={{ mb: 0, mt: 4 }}
                withoutWrapperCSS
              />
            )}

            {ticketType === 'internal_user' && (
              <Box mt={4} display="block">
                <ReactSelectUsers
                  key={`${id}:${attendee?.user_id}`}
                  // @ts-expect-error: types
                  searchType="event-registration"
                  designSystemStyle
                  label={__('Choose attendee')}
                  required
                  onChange={onUserSelect}
                  withAvatars
                  defaultValue={
                    attendee?.user_id
                      ? {
                          label: attendee?.firstname,
                          value: attendee?.user_id,
                          data: {
                            photo_url: attendee?.metadata?.event?.photo_url,
                          },
                        }
                      : undefined
                  }
                  admin={false}
                  placeholder={__('Select attendee')}
                  defaultOptions
                  isSearchable
                />
              </Box>
            )}

            {index === 0 && (
              <Box flexDirection="column">
                {event?.show_participants &&
                  attendee.user_id === currentUser.id.toString() && (
                    <Box mr="auto" mt={4}>
                      <Checkbox
                        name={`attendees[${index}][hidden]`}
                        label={__('Hide me from the list of participants')}
                        labelMultiline
                        wrapperProps={{ mb: 0 }}
                      />
                    </Box>
                  )}
                {event?.same_attendee_for_all_tickets_enabled &&
                  attendees.length > 1 && (
                    <Box mt={4}>
                      <FakeLink onClick={onPrefill}>
                        {__('Same attendee for all tickets')}
                      </FakeLink>
                    </Box>
                  )}
              </Box>
            )}
          </Box>
          {expanded && (
            <>
              {ticketType !== 'internal_user' && (
                <>
                  <Text
                    name={`attendees[${index}][firstname]`}
                    label={__('First name')}
                    onChange={(firstname) => onUpdate({ firstname })}
                    validate={{
                      required: true,
                    }}
                    wrapperProps={{ mb: 0 }}
                    onBlur={() => syncAttendeeField('firstname', index)}
                  />
                  <Text
                    name={`attendees[${index}][lastname]`}
                    label={__('Last name')}
                    onChange={(lastname) => onUpdate({ lastname })}
                    validate={{
                      required: true,
                    }}
                    wrapperProps={{ mb: 0 }}
                    onBlur={() => syncAttendeeField('lastname', index)}
                  />

                  <Email
                    name={`attendees[${index}][email]`}
                    label={__('Email address')}
                    onChange={(email) => onUpdate({ email })}
                    validate={{
                      required: true,
                    }}
                    wrapperProps={{ mb: 0 }}
                    onBlur={() => syncAttendeeField('email', index)}
                  />
                </>
              )}

              {standardFields && (
                <StandardFields
                  fields={standardFields}
                  name={`attendees[${index}]`}
                  onUpdate={onUpdate}
                  onBlur={(name) => syncAttendeeField(name, index)}
                />
              )}

              {customizableAttributes &&
                customizableAttributes.length > 0 &&
                customizableAttributes.map((attribute) => (
                  <CustomAttribute
                    key={attribute.id}
                    name={`attendees[${index}][${attribute.name}]`}
                    customAttribute={attribute}
                    // @ts-expect-error: fix types
                    labelMultiline
                    onChange={(v) => {
                      if (attribute.attribute_type === 'select') {
                        return onUpdate({
                          [attribute.name]: Array.isArray(v)
                            ? v.map((v) => v.value)
                            : [v.value],
                        });
                      }

                      return onUpdate({
                        [attribute.name]: v,
                      });
                    }}
                    wrapperProps={{
                      mb: 0,
                      gridColumn: {
                        _: 'auto',
                        m:
                          attribute.attribute_type === 'double_select'
                            ? '1 / 3'
                            : 'auto',
                      },
                    }}
                  />
                ))}
            </>
          )}
        </Section>
        <TicketAddons addons={attendee.addons} ticketIndex={index} />
      </Box>
    </CardWithTitle>
  );
};
